/**
 * 底部输入栏：
 * - 文本：Enter 发送，Shift+Enter 换行
 * - 表情：Popover 插入 Unicode（仍当文本发）
 * - 图片 / 视频 / 文件：隐藏 input + 回调给 hook
 */
import { useRef, useState } from "react";
import { Button, Flex, Input, Popover, Space, theme, message } from "antd";
import {
  PictureOutlined,
  VideoCameraOutlined,
  PaperClipOutlined,
  SmileOutlined,
} from "@ant-design/icons";

type Props = {
  disabled?: boolean;
  /** 发文本（含表情字符） */
  onSend: (text: string) => Promise<void>;
  onSendImage: (file: File) => Promise<void>;
  onSendVideo: (file: File) => Promise<void>;
  onSendFile: (file: File) => Promise<void>;
};

/** 常用表情；需要更多可自行加 */
const EMOJIS = [
  "😀",
  "😁",
  "😂",
  "🤣",
  "😊",
  "😍",
  "😘",
  "😜",
  "🤔",
  "😎",
  "😢",
  "😭",
  "😡",
  "👍",
  "👎",
  "👏",
  "🙏",
  "🔥",
  "🎉",
  "❤️",
  "💯",
  "✨",
  "🤝",
  "👀",
  "💪",
  "☕",
  "🍺",
  "🏠",
  "📌",
  "✅",
];

export function Composer({
  disabled,
  onSend,
  onSendImage,
  onSendVideo,
  onSendFile,
}: Props) {
  const { token } = theme.useToken();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const imageRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const submitText = async () => {
    const t = text.trim();
    if (!t || busy || disabled) return;
    setBusy(true);
    try {
      await onSend(t);
      setText("");
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** 统一跑媒体发送，防止连点 */
  const runMedia = async (fn: () => Promise<void>) => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const insertEmoji = (emoji: string) => {
    setText((prev) => prev + emoji);
  };

  const emojiPanel = (
    <div style={{ width: 240, maxHeight: 160, overflow: "auto" }}>
      <Space wrap size={4}>
        {EMOJIS.map((e) => (
          <Button
            key={e}
            type="text"
            size="small"
            style={{ fontSize: 20, width: 36, height: 36, padding: 0 }}
            onClick={() => insertEmoji(e)}
          >
            {e}
          </Button>
        ))}
      </Space>
    </div>
  );

  return (
    <Flex
      vertical
      gap={token.marginXXS}
      style={{
        margin: token.margin,
        padding: token.paddingXS,
        background: token.colorBgContainer,
        borderRadius: 22,
      }}
    >
      {/* 工具条：表情 / 图 / 视频 / 文件 */}
      <Flex gap={4} align="center" style={{ paddingLeft: 4 }}>
        <Popover content={emojiPanel} trigger="click" placement="topLeft">
          <Button
            type="text"
            icon={<SmileOutlined />}
            disabled={disabled || busy}
          />
        </Popover>
        <Button
          type="text"
          icon={<PictureOutlined />}
          disabled={disabled || busy}
          onClick={() => imageRef.current?.click()}
        />
        <Button
          type="text"
          icon={<VideoCameraOutlined />}
          disabled={disabled || busy}
          onClick={() => videoRef.current?.click()}
        />
        <Button
          type="text"
          icon={<PaperClipOutlined />}
          disabled={disabled || busy}
          onClick={() => fileRef.current?.click()}
        />

        <input
          ref={imageRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void runMedia(() => onSendImage(f));
          }}
        />
        <input
          ref={videoRef}
          type="file"
          accept="video/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void runMedia(() => onSendVideo(f));
          }}
        />
        <input
          ref={fileRef}
          type="file"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void runMedia(() => onSendFile(f));
          }}
        />
      </Flex>

      <Flex align="flex-end" gap={token.marginXS}>
        <Input.TextArea
          value={text}
          disabled={disabled || busy}
          autoSize={{ minRows: 1, maxRows: 4 }}
          variant="borderless"
          placeholder={disabled ? "离线时不能发送" : "输入消息，可点😊插表情"}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submitText();
            }
          }}
        />
        <Button
          type="primary"
          shape="round"
          loading={busy}
          disabled={disabled || !text.trim()}
          onClick={() => void submitText()}
        >
          发送
        </Button>
      </Flex>
    </Flex>
  );
}
